import React from 'react';
import { Link } from 'react-router-dom';
import StaticPageLayout from '../components/StaticPageLayout';
import { ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const SubmissionProcessPage = () => {
  const { user } = useAuth();

  return (
    <StaticPageLayout
      overline="Submission Policies"
      title="Submission Process"
      description="A step-by-step guide to submitting your research manuscript to MAH Quantum Research Institute. The process is structured to ensure clarity, efficiency, and editorial integrity."
      breadcrumbs={[{ label: 'Submission Process', path: '/submission-process' }]}
    >
      <div className="space-y-10">

        {/* ================= STEP PROCESS ================= */}
        <section>
          <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-5 pb-3 border-b border-[#E5E7EB]">
            Step-by-Step Process
          </h2>

          <div className="space-y-3">
            {[
              {
                step: '01',
                title: 'Select the Appropriate Journal',
                desc: 'Review the aims and scope of available journals and select the one most relevant to your research area. Submissions outside the journal scope may be declined without peer review.'
              },
              {
                step: '02',
                title: 'Prepare Your Manuscript',
                desc: 'Format your manuscript in accordance with the Author Guidelines. Ensure completeness of abstract, keywords, author affiliations, references, and all supporting materials prior to submission.'
              },
              {
                step: '03',
                title: 'Prepare Cover Letter',
                desc: 'Provide a formal cover letter outlining the originality, significance, and relevance of the research. Authors must confirm that the manuscript is not under consideration elsewhere.'
              },
              {
                step: '04',
                title: 'Create an Account / Sign In',
                desc: 'Register for an author account or sign in to access the submission system. This enables manuscript tracking, communication with editors, and status updates.'
              },
              {
                step: '05',
                title: 'Complete the Submission Form',
                desc: 'Complete all mandatory metadata fields including manuscript title, structured abstract, keywords, author details, co-authors, and journal selection. Upload the manuscript file in the required format.'
              },
              {
                step: '06',
                title: 'Review and Submit',
                desc: 'Carefully verify all submission details before final confirmation. Upon successful submission, a unique Manuscript ID will be generated for tracking and correspondence.'
              }
            ].map(item => (
              <div key={item.step} className="flex gap-4 border border-[#E5E7EB] p-5 bg-white">
                <span className="font-serif text-3xl font-bold text-[#E5E7EB] leading-none flex-shrink-0 w-10">
                  {item.step}
                </span>
                <div>
                  <h3 className="font-semibold text-[#1F1F1F] text-sm mb-1">
                    {item.title}
                  </h3>
                  <p className="text-sm text-[#6B7280] leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ================= AFTER SUBMISSION ================= */}
        <section>
          <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-4 pb-3 border-b border-[#E5E7EB]">
            After Submission
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              {
                label: 'Acknowledgement',
                value: 'Within 24 hours',
                desc: 'A confirmation email containing your Manuscript ID will be issued upon successful submission.'
              },
              {
                label: 'Editorial Screening',
                value: '3–5 business days',
                desc: 'Initial evaluation of scope alignment, submission completeness, and basic quality criteria.'
              },
              {
                label: 'Peer Review',
                value: '4–8 weeks',
                desc: 'Double-blind peer review conducted by qualified experts to assess technical quality and contribution.'
              }
            ].map(item => (
              <div key={item.label} className="border border-[#E5E7EB] bg-[#F5F7FA] p-4">
                <p className="font-serif text-base font-bold text-[#1A73E8] mb-0.5">
                  {item.value}
                </p>
                <p className="text-xs font-semibold text-[#374151] mb-1.5">
                  {item.label}
                </p>
                <p className="text-xs text-[#6B7280] leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* ================= CTA ================= */}
        <div className="flex gap-3">
          <Link
            to={user ? '/submit' : '/login'}
            className="flex items-center gap-2 px-6 py-3 bg-[#1A73E8] text-white text-sm font-medium hover:bg-[#174EA6] transition-colors"
          >
            Proceed to Manuscript Submission <ArrowRight size={14} />
          </Link>

          <Link
            to="/author-guidelines"
            className="flex items-center gap-2 px-6 py-3 border border-[#1A73E8] text-[#1A73E8] text-sm font-medium hover:bg-[#F5F7FA] transition-colors"
          >
            Author Guidelines
          </Link>
        </div>

      </div>
    </StaticPageLayout>
  );
};

export default SubmissionProcessPage;
